import { ClickAwayListener } from '@material-ui/core';
import type { Placement, VirtualElement } from '@popperjs/core';
import { InfoTooltip } from '@ps/web-shared-components';
import classNames from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import { select } from '@swf/core/store';

import {
  arrow,
  coachMarkEnter,
  coachMarkEnterActive,
  coachMarkExit,
  coachMarkExitActive,
  container,
  refElement
} from './CoachMark.scss';

const { info, tooltip, tooltipArrow } = InfoTooltip.styles;

export type TooltipElement = HTMLDivElement | null;

export const transition = {
  enter: coachMarkEnter,
  enterActive: coachMarkEnterActive,
  exit: coachMarkExit,
  exitActive: coachMarkExitActive
};

interface Props {
  children?: React.ReactElement;
  content: React.ReactNode;
  placement?: Placement;
  className?: string;
  refElementClassName?: string;
  animation?: boolean;
  onClickAway?: () => void;
  forwardedRef?: Element | VirtualElement | undefined;
  classes?: { arrow?: string; refElement?: string; container?: string };
}

const CoachMark: React.FC<Props> = ({
  children,
  content,
  placement,
  className,
  refElementClassName,
  animation = true,
  onClickAway,
  forwardedRef,
  classes = { arrow, container, refElement }
}) => {
  const isMobile = useSelector(select.device.isMobile);
  const [referenceElement, setReferenceElement] = useState<TooltipElement>(null);
  const [popperElement, setPopperElement] = useState<TooltipElement>(null);
  const [arrowElement, setArrowElement] = useState<TooltipElement>(null);
  const tooltipPointer = forwardedRef ?? referenceElement;
  const nodeRef = React.useRef(null);
  const { styles, attributes, update } = usePopper(tooltipPointer, popperElement, {
    placement,
    strategy: 'fixed',
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: {
          offset: [0, 15]
        }
      },
      {
        name: 'preventOverflow',
        options: {
          mainAxis: true,
          altAxis: true,
          padding: 16
        }
      }
    ]
  });

  const [showCoachMark, setShowCoachMark] = useState(false);
  const refreshCoachMark = useCallback(() => {
    if (showCoachMark && update) {
      update();
    }
  }, [showCoachMark, update]);

  useEffect(() => {
    const observer = new MutationObserver(() => refreshCoachMark());

    observer.observe(document, {
      childList: true,
      subtree: true
    });

    return () => observer.disconnect();
  }, [refreshCoachMark]);

  useEffect(() => {
    setShowCoachMark(true);
    return () => setShowCoachMark(false);
  }, []);

  if (isMobile && children) return children;

  const contentWrapper = (
    <ClickAwayListener
      onClickAway={() => {
        onClickAway && onClickAway();
      }}
    >
      <div
        className={classNames(tooltip, classes.container, className)}
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
      >
        {content}
        <div
          className={classNames(tooltipArrow, classes.arrow)}
          ref={setArrowElement}
          style={styles.arrow}
        />
      </div>
    </ClickAwayListener>
  );

  return (
    <>
      {children && (
        <div
          className={classNames(info, classes.refElement, refElementClassName)}
          ref={setReferenceElement}
        >
          {children}
        </div>
      )}
      {animation ? (
        <CSSTransition
          in={showCoachMark}
          classNames={transition}
          timeout={1000}
          unmountOnExit
          nodeRef={nodeRef}
        >
          <div ref={nodeRef}>{contentWrapper}</div>
        </CSSTransition>
      ) : (
        contentWrapper
      )}
    </>
  );
};

export default CoachMark;
